'use client';

import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchmove',
  'scroll',
  'focusin',
] as const;

// Activity re-arms the timer at most this often (scroll/wheel fire in bursts).
const THROTTLE_MS = 500;

type UseIdleTimerOptions = {
  timeoutMs: number;
  enabled?: boolean;
  onIdle: () => void;
};

/**
 * Calls `onIdle` once after `timeoutMs` with no user activity. Any activity
 * event restarts the countdown; a hidden tab pauses it and a visible tab
 * restarts a full countdown. Disarms after firing.
 */
export function useIdleTimer({
  timeoutMs,
  enabled = true,
  onIdle,
}: UseIdleTimerOptions): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastArmedAt = 0;

    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const detach = () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, true),
      );
      document.removeEventListener('visibilitychange', onVisibility);
    };

    const arm = () => {
      clear();
      lastArmedAt = performance.now();
      timer = setTimeout(() => {
        timer = null;
        detach();
        onIdleRef.current();
      }, timeoutMs);
    };

    function onActivity() {
      if (document.visibilityState === 'hidden') return;
      if (performance.now() - lastArmedAt < THROTTLE_MS) return;
      arm();
    }

    function onVisibility() {
      if (document.visibilityState === 'hidden') clear();
      else arm();
    }

    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, {
        passive: true,
        capture: true,
      }),
    );
    document.addEventListener('visibilitychange', onVisibility);
    if (document.visibilityState !== 'hidden') arm();

    return () => {
      clear();
      detach();
    };
  }, [timeoutMs, enabled]);
}
